import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useGetDepartments } from "@/hooks/departments";
import {
  CreateService,
  PaymentModel,
  PaymentModelList,
  ServiceType,
  ServiceTypeList,
} from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function Form({
  model,
  onUpsert,
}: {
  model?: CreateService;
  onUpsert: (data: CreateService) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { data: departments, isLoading: departmentsLoading } =
    useGetDepartments();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateService>>(() => {
    const config: FormOptions<CreateService> = {
      name: {
        value: model?.name ?? "",
        validator: z.string().min(1, "errors.required"),
      },
      type: {
        value: model?.type,
        validator: z.nativeEnum(ServiceType, {
          required_error: "errors.required",
        }),
      },
      departmentId: {
        value: model?.departmentId,
        validator: z.number().nullish(),
      },
      description: {
        value: model?.description,
        validator: z.string().nullish(),
      },
      referencePrice: {
        value: model?.referencePrice,
        validator: z.number().nullish(),
      },
      paymentModel: {
        value: model?.paymentModel,
        validator: z.nativeEnum(PaymentModel, {
          required_error: "errors.required",
        }),
      },
    };
    return config;
  }, [model]);

  return (
    <DomainUpsert
      title={model?.name ?? t("services.new.title")}
      buttonTranslation={
        isEdit ? "services.update.button" : "services.new.button"
      }
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "name",
          label: "services.fields.name",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.name}
              onChange={(ev) => {
                form.setValue("name", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "type",
          label: "services.fields.type",
          input: (form, id) => (
            <SelectSearch
              id={id}
              value={form.value.type ?? undefined}
              onChange={(v) => {
                form.setValue("type", v);
              }}
              options={ServiceTypeList.map((v) => ({
                value: v,
                label: t(`services.types.${v}`),
              }))}
            />
          ),
        },
        {
          key: "description",
          label: "services.fields.description",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.description ?? undefined}
              onChange={(ev) => {
                form.setValue("description", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "referencePrice",
          label: "services.fields.referencePrice",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.referencePrice?.toString()}
              type="number"
              min={0}
              onChange={(ev) => {
                form.setValue(
                  "referencePrice",
                  Number.parseFloat(ev.target.value),
                );
              }}
            />
          ),
        },

        {
          key: "paymentModel",
          label: "services.fields.paymentModel",
          input: (form, id) => (
            <SelectSearch
              id={id}
              value={form.value.paymentModel ?? undefined}
              onChange={(v) => {
                form.setValue("paymentModel", v);
              }}
              options={PaymentModelList.map((v) => ({
                value: v,
                label: t(`services.paymentModels.${v}`),
              }))}
            />
          ),
        },

        {
          key: "departmentId",
          label: "services.fields.departmentId",
          input: (form, id) =>
            departmentsLoading || !departments ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <SelectSearch
                id={id}
                className="w-full"
                value={form.value.departmentId}
                onChange={(v) => {
                  form.setValue("departmentId", v);
                }}
                options={departments.map((g) => ({
                  label: g.name,
                  value: Number.parseInt(g.id),
                }))}
              />
            ),
        },
      ]}
    />
  );
}
